import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { privateRequest } from "../utils/api";
import { useDispatch } from "react-redux";
import { updateUser } from "../store/auth/authSlice";
import type { AppDispatch } from "../store";
import type { User } from "./userService";

export interface UpdateProfileData {
  name?: string;
  email?: string;
  locale?: string;
  avatar?: string;
  bio?: string;
}

export interface ChangePasswordData {
  current_password: string;
  password: string;
  password_confirmation: string;
}

// Get authenticated user profile
export const useProfile = () => {
  return useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const response = await privateRequest.get<{
        success: boolean;
        data: { user: User };
        message: string;
      }>("/auth/profile");
      return response.data.data.user;
    },
  });
};

// Update profile mutation
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      const response = await privateRequest.put<{
        success: boolean;
        data: { user: User };
        message: string;
      }>("/auth/profile", data);
      return response.data.data.user;
    },
    onSuccess: (user) => {
      // Update Redux store with new user data
      dispatch(updateUser(user));
      // Invalidate profile query to refetch
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};

// Change password mutation
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: ChangePasswordData) => {
      const response = await privateRequest.put<{
        success: boolean;
        data: null;
        message: string;
      }>("/auth/change-password", data);
      return response.data;
    },
  });
};

